import { TFunction } from 'react-i18next';
import { CellChange, SpreadsheetProps, TableData } from 'types/spreadsheet';
import { isDevelopment } from 'utils/environment';
import { isArray } from 'utils/object';
import {
  ChartDataSeries,
  ChartTableColumnSeries,
  ControlledHeader,
  isControlledHeader,
} from 'widgets/ChartWidgets/ChartWidget.types';

/**
 * Since the table may have header, so the chart data should be updated from index - headerRowNumber
 *
 * Ex: Handsontable has Data where h{x} as the header row and {x} as the data row
 * [['h1', 0], ['h2', 1], ['h3', 2], ['h4', 3]]
 *
 * The result chart data series would be [
 *  {name: 'h1', data: [0]},
 *  {name: 'h2', data: [1]},
 *  {name: 'h3', data: [2]},
 *  {name: 'h4', data: [3]},
 * ]
 *
 * If we change two rows on the Handsontable from index 0, and we define headerRowNumber to be 1,
 * This means we are changing a header row and a data row
 * Therefore, the index on the chart data series should be
 *  index - headerRowNumber >= 0 ? index - headerRowNumber : 0
 * and the amount of the data on the chart data series should be
 *  index - headerRowNumber < 0 ? amount + index - headerRowNumber : amount
 *
 * @param index the row index of the table starting to be changed
 * @param amount the amount of rows to be changed
 * @param headerRowNumber number of header rows in the table
 * @returns
 */
export const getValidatedIndexAndAmount = (index: number, amount: number, headerRowNumber: number) => {
  const validatedIndex = index - headerRowNumber >= 0 ? index - headerRowNumber : 0;
  const validatedAmount = index - headerRowNumber < 0 ? amount + index - headerRowNumber : amount;
  return { validatedIndex, validatedAmount };
};

/**
 * Adds a new row to the seriesData for a chart widget.
 *
 * @param {ChartDataSeries} seriesData - The original chart data series.
 * @param {number} index - The position at which the new row will be added.
 * @param {number} amount - The amount of empty data points to be added in the new row.
 * @param {number} headerRowNumber - The total number of rows as column header in the table.
 * @returns {ChartDataSeries} - Returns updated chart data series with the added row.
 */
export const addRow = (seriesData: ChartDataSeries, index: number, amount: number, headerRowNumber: number) => {
  const updatedSeriesData = seriesData.map(({ data, ...otherData }) => {
    const copiedData = [...data];

    const { validatedIndex, validatedAmount } = getValidatedIndexAndAmount(index, amount, headerRowNumber);
    const emptyRow = Array(validatedAmount).fill('');
    copiedData.splice(validatedIndex, 0, ...emptyRow);

    return { ...otherData, data: copiedData };
  });

  return updatedSeriesData as ChartDataSeries;
};

/**
 * Adds a new column to the seriesData for a chart widget.
 *
 * @param {ChartDataSeries} seriesData - The original chart data series.
 * @param {number} index - The position at which the new column will be added.
 * @param {number} amount - The amount of empty data points to be added in the new column.
 * @param {number} tableRowCount - The total number of rows in the table.
 * @param {number} headerRowNumber - The total number of rows as column header in the table.
 * @returns {ChartDataSeries} - Returns updated chart data series with the added column.
 */
export const addColumn = (
  seriesData: ChartDataSeries,
  index: number,
  amount: number,
  tableRowCount: number,
  headerRowNumber: number,
  options: {
    shouldAppendControlHeader?: boolean;
  } = {},
) => {
  const { shouldAppendControlHeader } = options;
  const updatedSeriesData = [...seriesData];

  // There may be rows for header, the actual position in the data array should be index - headerRowNumber
  const emptyColumnData = Array(tableRowCount > headerRowNumber ? tableRowCount - headerRowNumber : 0).fill('');

  const filledData: ChartDataSeries[number] = { name: '', data: emptyColumnData };

  // Currently, only the combo chart would support the control header.
  // When inserting to the first column, we assume that is the Y1.
  // Note: the index is 0-based, the column at the index 0 is the header column.
  if (shouldAppendControlHeader && index === 1) {
    filledData.controlledHeader = ControlledHeader.ComboColumn;
  }

  // For all other new columns, the controlled header should be the same as the left column.
  if (shouldAppendControlHeader && index > 1) {
    filledData.controlledHeader = seriesData[index - 1].controlledHeader;
  }

  // Insert new column(s) at the specified index
  const emptyColumn = Array(amount).fill(filledData);
  updatedSeriesData.splice(index, 0, ...emptyColumn);

  return updatedSeriesData as ChartDataSeries;
};

/**
 * Removes a row from the seriesData for a chart widget.
 *
 * @param {ChartDataSeries} seriesData - The original chart data series.
 * @param {number} amount - The amount of rows to be removed.
 * @param {number} index - The position from which the rows will be removed.
 * @param {number} headerRowNumber - The total number of rows as column header in the table.
 * @returns {ChartDataSeries} - Returns updated chart data series with the removed row.
 */
export const removeRow = (seriesData: ChartDataSeries, amount: number, index: number, headerRowNumber: number) => {
  const updatedSeriesData = seriesData.map(({ data, ...otherData }) => {
    const copiedData = [...data];

    // Remove specified amount of rows starting from the given index
    const { validatedIndex, validatedAmount } = getValidatedIndexAndAmount(index, amount, headerRowNumber);
    copiedData.splice(validatedIndex, validatedAmount);
    return { ...otherData, data: copiedData };
  });

  return updatedSeriesData as ChartDataSeries;
};

/**
 * Removes a column from the seriesData for a chart widget.
 *
 * @param {ChartDataSeries} seriesData - The original chart data series.
 * @param {number} amount - The amount of columns to be removed.
 * @param {number} index - The position from which the columns will be removed.
 * @returns {ChartDataSeries} - Returns updated chart data series with the removed column.
 */
export const removeColumn = (seriesData: ChartDataSeries, amount: number, index: number) => {
  const updatedSeriesData = [...seriesData];
  // Remove specified amount of columns starting from the given index
  updatedSeriesData.splice(index, amount);

  return updatedSeriesData as ChartDataSeries;
};

/**
 * Updates the cell values in the provided series data.
 *
 * @param {ChartDataSeries} seriesData - The series data to update.
 * @param {CellChange[]} changes - An array of changes to apply. Each change is a tuple of the format [row, column, previousValue, nextValue].
 * @param {number} headerRowNumber - The total number of rows as column header in the table.
 * @returns {object} An object containing two properties: isUpdated, a boolean indicating if seriesData has been updated or not; and updatedSeriesData, the updated series data.
 */
export const updateCell = (seriesData: ChartDataSeries, changes: CellChange[], headerRowNumber: number) => {
  // track whether the seriesData has been updated or not
  let isUpdated = false;

  // Create a copy of the seriesData if there are changes, otherwise use the original seriesData
  const updatedSeriesData: ChartDataSeries = changes.length > 0 ? [...seriesData] : seriesData;

  changes.forEach(([row, col, prevValue, nextValue]) => {
    // Not gonna to support array of objects for now
    if (typeof col === 'string') return;

    // We only allow 1 or 0 header row for now
    if (headerRowNumber > 1) return;

    // Check if it is the header row
    if (row < headerRowNumber) {
      updatedSeriesData[col] = { ...updatedSeriesData[col], name: nextValue };
      isUpdated = true;
      return;
    }

    // Check if the previous value is different from the next value
    const notNullNext = nextValue ?? '';
    if (prevValue !== notNullNext) {
      if (!updatedSeriesData?.[col]?.data) return;

      const copiedColumn = [...updatedSeriesData[col].data];

      // Since the first row is the header, so the row number on the data structure should be -1
      copiedColumn.splice(row - headerRowNumber, 1, notNullNext);
      updatedSeriesData[col] = { ...updatedSeriesData[col], data: copiedColumn };

      isUpdated = true;
    }
  });

  return { isUpdated, updatedSeriesData };
};

/**
 * Converts the table data into chart widget series data
 *
 * @param {TableData} tableData - The table data to convert into series data.
 * @param {number} headerRowNumber - The total number of rows as column header in the table.
 * @param {boolean | string[] | ((index: number) => string)} colHeaders - The column headers used by handsontable.
 * @returns {ChartDataSeries} - The series data generated from the table data.
 */
export function tableDataToSeriesData(
  tableData: TableData,
  headerRowNumber: number,
  colHeaders?: boolean | string[] | ((index: number) => string),
): ChartDataSeries {
  const updatedSeriesData: ChartDataSeries = [];

  // iterate over the table data based on rows
  //    e.g.
  //   ['', '2010', '2020', '2030'],
  //   ['Public Sector', 18, 24, 30],
  //   ['Education', 12, 16, 20],
  //   ['Technology', 6, 8, 10],
  //   ['', '', '', ''],

  // turn the table data into series data - based on columns
  //   [
  //     { data: [ 'Public Sector', 'Education', 'Technology' ] },
  //     { name: '2010', data: [18, 12, 6] },
  //     { name: '2020', data: [24, 16, 8] },
  //     { name: '2030', data: [30, 20, 10] },
  //     { data: ['', '', ''] },
  //   ];

  for (let rowIdx = 0; rowIdx < tableData.length; rowIdx++) {
    const row = tableData[rowIdx];

    for (let colIdx = 0; colIdx < row.length; colIdx++) {
      const cell = row[colIdx];

      // first few ros may be the header row
      if (rowIdx < headerRowNumber) {
        if (rowIdx === 0) {
          // Create an empty data object for the header column
          updatedSeriesData[colIdx] = { data: [] } as ChartTableColumnSeries;

          // If the cell is not empty, add the name to the header column
          if (cell) updatedSeriesData[colIdx].name = cell;
        } else {
          // We support only 1 header row for now
          if (isDevelopment) {
            throw new Error('We do not support multiple header row for now');
          }
        }

        continue;
      }

      if (rowIdx === 0) {
        // Create an empty data object for each non-header row
        updatedSeriesData[colIdx] = { data: [cell ?? ''] } as ChartTableColumnSeries;

        continue;
      }

      // in case the rows are of different lengths
      if (!updatedSeriesData[colIdx]) updatedSeriesData[colIdx] = { data: [] } as ChartTableColumnSeries;

      updatedSeriesData[colIdx].data.push(cell);
    }
  }

  // For map: If headerRowNumber is zero and there are colHeaders, colHeaders are set as `name` variables to seriesData.
  if (headerRowNumber === 0 && colHeaders && typeof colHeaders === 'object' && isArray(colHeaders)) {
    colHeaders.forEach((header: string | undefined, index: number) => {
      updatedSeriesData[index].name = header;
    });
  }

  return updatedSeriesData as ChartDataSeries;
}

interface ComputerColumnHeadersOptions {
  t?: TFunction<'editor_side_menu_settings', 'chartSettings'>;
  shouldAppendControlHeader?: boolean;
}

/**
 * Computes the column headers for the HotTable component.
 * It is generated from the ChartDataSeries's data such as name or the control header.
 *
 * @param seriesData - The series data used to generate the column headers.
 * @param t - The translation function that is used to translate the control header.
 * @returns The column headers as an array of strings, a boolean value, or a function.
 */
export const computeColumnHeaders = (
  seriesData: ChartDataSeries,
  options: ComputerColumnHeadersOptions = {},
): SpreadsheetProps['colHeaders'] => {
  const { t, shouldAppendControlHeader } = options ?? {};
  const headers = seriesData.map(({ controlledHeader, name }, idx) => {
    // For now, omly the combo chart is using the controlled header, and its column is pre-defined to be "X'.
    if (shouldAppendControlHeader && idx === 0) {
      return 'X';
    }

    if (shouldAppendControlHeader && isControlledHeader(controlledHeader) && t) {
      return t(`controlledHeader.${controlledHeader}`);
    }

    return name ?? '';
  });

  return headers;
};

/**
 * Updates the controlled header of a column in the series data array.
 *
 * @param {ChartDataSeries} seriesData - Array of data series.
 * @param {number} colIndex - Index of the column to update.
 * @param {string} value - New controlled header value.
 * @returns {ChartDataSeries} Updated series data array.
 * @throws {Error} If value is invalid and in development mode.
 */
export const updateControlledHeader = (seriesData: ChartDataSeries, colIndex: number, value: string) => {
  if (isControlledHeader(value) && seriesData.length > colIndex && seriesData[colIndex]?.controlledHeader !== value) {
    const updatedSeriesData = [...seriesData];
    updatedSeriesData[colIndex] = { ...seriesData[colIndex], controlledHeader: value };
    return updatedSeriesData;
  }

  if (!isControlledHeader(value) && isDevelopment) {
    throw new Error('The value trying to add is not a valid control header. Please update the ControlledHeaderType');
  }

  return seriesData;
};
